/**
 * Lightweight markdown-to-HTML converter for ticket descriptions and AI summaries.
 * Supports: headers, bold, italic, lists, links, code blocks, line breaks.
 *
 * SECURITY: the result is injected with dangerouslySetInnerHTML, and the input is
 * NOT trusted — ticket text is written by any employee or automated alarm, and AI
 * summaries are derived from that same text. So the input is HTML-escaped before
 * any markdown transform runs, and link targets are restricted to safe schemes.
 * Every tag in the output is one this function generated.
 *
 * Blocks are assembled line by line rather than by chaining replacements over the
 * whole string. That is what keeps lists out of paragraphs: a <ul> inside a <p> is
 * invalid HTML, and the browser silently closes the <p> early, so the rendered DOM
 * stops matching the markup and picks up stray empty paragraphs.
 */

interface RenderOptions {
  /**
   * Render `#` headings as bold text instead of <h1>-<h4>. For constrained
   * contexts such as table cells: this app ships no stylesheet, so a real
   * heading renders at browser default size and blows out the row height.
   */
  flattenHeadings?: boolean;
}

/**
 * Section headers the summariser is instructed to emit. The few-shot example in
 * ai-config produces these as bare lines with NO markdown marker, so without
 * this the app's own canonical summary format renders as an undifferentiated
 * wall of text. This is honouring a defined output contract, not guessing at
 * one, which is why it is an explicit list and not a shape heuristic.
 */
const SECTION_LABELS = new Set([
  'summary',
  'incident summary',
  'problem summary',
  'root cause',
  'resolution',
  'root cause/resolution',
  'action items',
  'timeline',
  'timeline of actions',
  'additional notes',
]);

const CODE_PLACEHOLDER = '\u0000CODE';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Allow only targets that cannot execute script. Anything else (javascript:,
 * data:, vbscript:, or an unrecognized scheme) loses its link and is rendered as
 * plain text, so the content survives but the payload does not.
 */
function isSafeHref(href: string): boolean {
  const trimmed = href.trim();
  if (/^(https?:\/\/|mailto:)/i.test(trimmed)) return true;
  // Relative paths and in-page anchors, but not protocol-relative "//host".
  return /^[/#][^/]/.test(trimmed) || trimmed === '/';
}

/** Inline spans, applied within a single block of text. */
function inline(text: string): string {
  return text
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, label: string, href: string) =>
      isSafeHref(href) ? `<a href="${href}" target="_blank" rel="noopener">${label}</a>` : label,
    );
}

/** A bare line that names a section, with or without a trailing colon. */
function sectionLabel(line: string): string | null {
  const trimmed = line.trim();
  // A legacy banner form: "=== Summary ===".
  const banner = trimmed.match(/^=+\s*(.+?)\s*=+$/);
  if (banner) return banner[1];
  const withoutColon = trimmed.replace(/:$/, '');
  return SECTION_LABELS.has(withoutColon.toLowerCase()) ? trimmed : null;
}

export function renderMarkdown(text: string, options: RenderOptions = {}): string {
  if (!text) return '';

  // Pull fenced code out first so its contents are never treated as markup.
  const codeBlocks: string[] = [];
  const withoutFences = escapeHtml(text).replace(/```([\s\S]*?)```/g, (_m, code: string) => {
    codeBlocks.push(code);
    return `\n${CODE_PLACEHOLDER}${codeBlocks.length - 1}\u0000\n`;
  });

  const blocks: string[] = [];
  let paragraph: string[] = [];
  let listItems: string[] = [];

  const flush = () => {
    if (paragraph.length) {
      blocks.push(`<p>${inline(paragraph.join('<br/>'))}</p>`);
      paragraph = [];
    }
    if (listItems.length) {
      blocks.push(`<ul>${listItems.map((i) => `<li>${inline(i)}</li>`).join('')}</ul>`);
      listItems = [];
    }
  };

  for (const line of withoutFences.split('\n')) {
    const code = line.match(new RegExp(`^${CODE_PLACEHOLDER}(\\d+)\u0000$`));
    if (code) {
      flush();
      blocks.push(`<pre><code>${codeBlocks[Number(code[1])]}</code></pre>`);
      continue;
    }

    if (!line.trim()) {
      flush();
      continue;
    }

    const listItem = line.match(/^[-*] (.+)$/) || line.match(/^\d+\. (.+)$/);
    if (listItem) {
      // No flush needed: flush() always emits the paragraph before the list, and
      // a paragraph line flushes any pending list first, so the two buffers can
      // only ever be non-empty with the paragraph genuinely first in the source.
      listItems.push(listItem[1]);
      continue;
    }

    const heading = line.match(/^(#{1,4}) (.+)$/);
    if (heading) {
      flush();
      const level = heading[1].length;
      const content = inline(heading[2]);
      blocks.push(
        options.flattenHeadings
          ? `<p><strong>${content}</strong></p>`
          : `<h${level}>${content}</h${level}>`,
      );
      continue;
    }

    const label = sectionLabel(line);
    if (label) {
      // Always bold rather than a real heading: these are labels within a
      // summary, and with no stylesheet a browser-default heading is oversized.
      flush();
      blocks.push(`<p><strong>${inline(label)}</strong></p>`);
      continue;
    }

    listItems.length && flush();
    paragraph.push(line);
  }

  flush();
  return blocks.join('');
}
